import React, { useEffect, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

import Header from '@components/Base/Header';
import TransactionList from '@components/Transactions';
import { getMixpanelProperties } from '@utils/helpers';
import { useAppDispatch, useAppSelector } from '@utils/hooks';
import {
  proviewConsoleTransactionReportDownloaded,
  proviewConsoleTransactionReportFilterApplied,
  proviewConsoleTransactionReportViewed,
} from '@utils/mixpanelActions';

import { formatInitialFilters, getFormattedFilters } from './helpers';
import { getCountriesAction, getTransactionsAction } from './slice';

export type AppliedFilters = {
  selectedStatus?: { label: string; value: string }[] | null;
  transactionId?: string;
  selectedDateRange?: {
    startDate: Date | null;
    endDate: Date | null;
  } | null;
};

const FinancialReport = () => {
  const dispatch = useAppDispatch();
  const {
    data: transactionsData,
    totalCount: transactionsTotalCount,
    isLoading: transactionsLoading,
    countries: { data: countriesData },
    transactionAllList: { isLoading: loadingCompleteTransactionList },
  } = useAppSelector((state) => state.transactionList);
  const { user, isMixpanelInitialized } = useAppSelector((state) => state.app);
  const [searchParams, setSearchParams] = useSearchParams();
  const parseInitialFilters = formatInitialFilters(searchParams);
  const filterDetails = parseInitialFilters();
  const { filters } = filterDetails;
  const [appliedFilters, updateAppliedFilters] = useState<AppliedFilters>(filters);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const triggerDownloadTransactionsListMpEvent = () => {
    if (isMixpanelInitialized && user) {
      const mixpanelProperties = getMixpanelProperties(user);
      const combinedProperties = {
        ...mixpanelProperties,
      };
      proviewConsoleTransactionReportDownloaded(combinedProperties);
    }
  };

  const downloadCompleteList = () => {
    const payload = {
      conditions: getFormattedFilters(appliedFilters as AppliedFilters),
      offset: 0,
      limit: null,
      isDownloadList: true,
    };
    dispatch(getTransactionsAction(payload));
    triggerDownloadTransactionsListMpEvent();
  };

  const triggerFilterMPEvent = (filters: AppliedFilters) => {
    if (isMixpanelInitialized && user) {
      const mixpanelProperties = getMixpanelProperties(user);
      const combinedProperties = {
        ...mixpanelProperties,
        filteredBy: { ...filters },
      };
      proviewConsoleTransactionReportFilterApplied(combinedProperties);
    }
  };

  const updateURLParams = () => {
    setSearchParams(
      (params) => {
        const filters = Object.entries(appliedFilters).reduce((acc: Record<string, unknown>, [key, value]) => {
          if (value) {
            if (
              Array.isArray(value) ? value.length > 0 : typeof value === 'object' ? Object.keys(value).length > 0 : true
            ) {
              acc[key] = value;
            }
          }
          return acc;
        }, {});

        if (Object.keys(filters).length > 0) {
          params.set('filters', JSON.stringify(filters));
        } else {
          params.delete('filters');
        }
        return params;
      },
      { replace: true },
    );
  };

  useEffect(() => {
    if (!transactionsData.length) {
      const payload = {
        conditions: getFormattedFilters(appliedFilters as AppliedFilters),
        limit: pagination.pageSize,
        offset: 0,
      };
      dispatch(getTransactionsAction(payload));
    }
    if (!countriesData.length) {
      dispatch(getCountriesAction());
    }
  }, []);

  useEffect(() => {
    const payload = {
      conditions: getFormattedFilters(appliedFilters as AppliedFilters),
      limit: pagination.pageSize,
      offset: 0,
    };
    updateURLParams();
    dispatch(getTransactionsAction(payload));
  }, [appliedFilters]);

  useEffect(() => {
    if (isMixpanelInitialized && user) {
      const mixpanelProperties = getMixpanelProperties(user);
      proviewConsoleTransactionReportViewed(mixpanelProperties);
    }
  }, [isMixpanelInitialized, user.tenant]);

  useEffect(() => {
    const payload = {
      conditions: getFormattedFilters(appliedFilters as AppliedFilters),
      limit: pagination.pageSize,
      offset: pagination.pageIndex * pagination.pageSize,
    };
    updateURLParams();
    dispatch(getTransactionsAction(payload));
  }, [pagination]);

  return (
    <>
      <Header details={{ label: 'Transaction' }} />
      <TransactionList
        transactions={transactionsData}
        countryList={countriesData}
        transactionsLoading={transactionsLoading}
        appliedFilters={appliedFilters}
        updateAppliedFilters={updateAppliedFilters}
        pagination={pagination}
        setPagination={setPagination}
        totalCount={transactionsTotalCount}
        downloadCompleteTransactionList={downloadCompleteList}
        loadingCompleteTransactionList={loadingCompleteTransactionList}
        triggerFilterMPEvent={triggerFilterMPEvent}
      />
    </>
  );
};

export default FinancialReport;
